<template>
  <NuxtLayout
    name="generic"
    header="standardDark"
    footer="standard"
  >
    <main
      v-if="data && data.pricing"
      :class="{
        'pricing-page': true,
      }"
    >
      <div
        :class="{
          'pricing-hero bg-www-color-neutral-off-white pb-10 pt-180px text-center text-www-color-neutral-medium-dark lg:pb-[110px]': true,
        }"
      >
        <PortableText
          v-if="data.pricing.heroContent.standardPortableText"
          :portable-data="data.pricing.heroContent.standardPortableText"
          :class="{
            'mb-12': true,
          }"
        />
      </div>

      <PlanSection
        v-if="data.pricing.plans"
        v-bind="data.pricing.plans"
      />

      <div class="flex justify-center py-6">
        <Button
          v-if="data.pricing.comparePlansButton"
          :button-props="{
            text: data.pricing.comparePlansButton,
            _type: 'button',
            style: 'secondary',
          }"
          @click="scrollToSection"
        />
      </div>

      <PortableTextCalloutWithIcon
        v-if="data.pricing.calloutsWithIcon"
        v-bind="data.pricing.calloutsWithIcon"
      />

      <CTABanner
        v-if="data.pricing.firstCTABanner?.reference"
        :data="data.pricing.firstCTABanner.reference"
      />

      <PortableTextContentCard
        v-if="data.pricing.contentCard"
        v-bind="data.pricing.contentCard"
        :class="{
          'mb-60px mt-11': true,
        }"
      />

      <PricingComparison
        ref="pricing-comparison"
        :compare-plans-header="data.pricing.comparePlansHeader"
        v-bind="{ ...data.pricing.plans, _type: 'highlightedPlans' }"
        page-type="pricing"
      />

      <CTABanner
        v-if="data.pricing.secondCTABanner?.reference"
        :data="data.pricing.secondCTABanner.reference"
      />
      <SectionBuilderFAQAccordion
        v-if="data.pricing.faqAccordion"
        v-bind="data.pricing.faqAccordion"
      />
    </main>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import type { QueryPricingResult } from '~/sanity/types';
import { queryPricing } from '~/queries/pages/PagePricing.query';
import { queryPricingResolved } from '~/queries/pages/PagePricingResolved.query';
import { useTemplateRef } from 'vue';
import PortableText from '~/components/Atoms/PortableText/PortableText.vue';
import PlanSection from '~/features/Pricing/components/PlanSection.vue';
import Button from '~/components/Atoms/Button/Button.vue';
import PortableTextCalloutWithIcon from '~/components/Molecules/CalloutsWithIcon/PortableTextCalloutWithIcon.vue';
import CTABanner from '~/components/Organisms/CTABanner/CTABanner.vue';
import PortableTextContentCard from '~/components/Molecules/ContentCard/PortableTextContentCard.vue';
import PricingComparison from '~/features/Pricing/components/PricingComparison.vue';
import SectionBuilderFAQAccordion from '~/components/Molecules/Accordion/SectionBuilderFAQAccordion.vue';
import type {
  ResolvedPlanEntry,
  ResolvedPlansQueryResult,
  StructuredDataOptions,
} from '~/utils/structuredDataTypes';
import { buildPricingPlanStructuredData } from '~/utils/structuredDataBuilder';

const { path: currentPathname } = useRoute();

const { data } = await useWwwSanityQueryNonNull<QueryPricingResult>(queryPricing, {
  currentPathname,
});

const { data: resolvedPlansData } = await useWwwSanityQueryNonNull<ResolvedPlansQueryResult>(
  queryPricingResolved,
  {
    currentPathname,
  }
);

const defaultCurrency = useState<string>('default-currency');
const targetSection = useTemplateRef('pricing-comparison');

const pricingPage = data.value?.pricing;

const pageTitle = pricingPage?.htmlTitle || pricingPage?.title;
const pageDescription = pricingPage?.ogDescription || pricingPage?.description;
const highlightedPlans = resolvedPlansData.value?.plans ?? [];

const resolvedPlans: ResolvedPlanEntry[] = highlightedPlans
  .map((highlightedPlan) => {
    const planRefId = highlightedPlan.selectPlan?.reference?._ref;
    const resolvedPlan = highlightedPlan.resolvedPlan;

    if (!planRefId || !resolvedPlan?.name) {
      return null;
    }

    return {
      plan: resolvedPlan,
      planRefId,
      resolvedPlanPrices: highlightedPlan.resolvedPlanPrices,
    };
  })
  .filter((resolvedEntry): resolvedEntry is ResolvedPlanEntry => resolvedEntry !== null);

const perPlanStructuredData: StructuredDataOptions[] = resolvedPlans.map(
  ({ plan, planRefId, resolvedPlanPrices }) =>
    buildPricingPlanStructuredData({
      plan,
      planRefId,
      defaultCurrency,
      resolvedPlanPrices,
    })
);

const scrollToSection = () => {
  targetSection.value?.$el.scrollIntoView({ behavior: 'smooth' });
};

usePageHead({
  _id: pricingPage?._id,
  description: pageDescription,
  ogImage: pricingPage?.ogImage?.asset ?? pricingPage?.cardImage?.asset,
  title: pageTitle,
  excludeFromExternalSearch: pricingPage?.excludeFromExternalSearch,
  twitterCard: 'summary',
  structuredData: [
    {
      schemaType: 'OfferCatalog',
      title: pageTitle,
      description: pageDescription,
      enabled: true,
    },
    ...perPlanStructuredData,
  ],
});
</script>

<style>
.pricing-hero p {
  margin-bottom: 7px !important;
}
.pricing-page .callouts-with-icon {
  padding: 80px 0 45px 0;
  background-color: var(--www-color-neutral-off-white);
}
.pricing-page .callouts-with-icon h3 {
  color: var(--www-color-neutral-medium-dark);
}
</style>
